import { captureApiFailure } from "../sentry";
import type { Attendee } from "./attendees";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.replace(/\/$/, "") ?? "";
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";
const functionSlug =
  import.meta.env.VITE_PICKLE_EVENT_FUNCTION?.trim() || "pickle-event";

const SESSION_TOKEN_KEY = "pickle.session.token";
const SESSION_ME_KEY = "pickle.session.me";
const SESSION_ROSTER_KEY = "pickle.session.roster";

export type AuthResult = {
  token: string;
  me: Attendee;
};

function functionUrl(): string {
  if (!supabaseUrl) {
    throw new Error("VITE_SUPABASE_URL is not configured");
  }
  return `${supabaseUrl}/functions/v1/${functionSlug}`;
}

function apiHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (anonKey) {
    headers.apikey = anonKey;
    headers.Authorization = `Bearer ${anonKey}`;
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function parseError(res: Response): Promise<string> {
  if (res.status === 404) {
    return "The sign-in service is not available yet (pickle-event API missing). Try again shortly.";
  }
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    if (body.error === "not_on_guest_list") {
      return "We can't find that email on the guest list. Make sure it's the one you registered with.";
    }
    if (body.error === "server_misconfigured") {
      return "The sign-in service is misconfigured. Contact the organizer.";
    }
    if (body.error) return body.error;
    if (body.message) return body.message;
  } catch {
    /* ignore */
  }
  return `Request failed (${res.status})`;
}

function wrapFetchError(err: unknown): Error {
  if (err instanceof TypeError && /fetch/i.test(err.message)) {
    return new Error(
      "Could not reach the sign-in service. Check your connection, or the pickle-event API may not be deployed yet.",
    );
  }
  return err instanceof Error ? err : new Error(String(err));
}

async function apiFetch(input: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    throw wrapFetchError(err);
  }
}

export async function authWithEmail(email: string): Promise<AuthResult> {
  const res = await apiFetch(functionUrl(), {
    method: "POST",
    headers: apiHeaders(),
    body: JSON.stringify({ email: email.trim() }),
  });
  if (!res.ok) {
    const err = new Error(await parseError(res));
    captureApiFailure("auth", err, { status: String(res.status) });
    throw err;
  }
  const data = (await res.json()) as AuthResult;
  persistSession(data.token, data.me, null);
  return data;
}

export async function fetchRoster(token: string): Promise<readonly Attendee[]> {
  const res = await apiFetch(functionUrl(), {
    method: "GET",
    headers: apiHeaders(token),
  });
  if (!res.ok) {
    const err = new Error(await parseError(res));
    captureApiFailure("roster", err, { status: String(res.status) });
    throw err;
  }
  const data = (await res.json()) as { attendees: Attendee[] };
  const roster = data.attendees ?? [];
  const tokenStored = getSessionToken();
  const me = getStoredMe();
  if (tokenStored && me) {
    persistSession(tokenStored, me, roster);
  }
  return roster;
}

export function persistSession(
  token: string,
  me: Attendee,
  roster: readonly Attendee[] | null,
): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    window.sessionStorage.setItem(SESSION_ME_KEY, JSON.stringify(me));
    if (roster) {
      window.sessionStorage.setItem(SESSION_ROSTER_KEY, JSON.stringify(roster));
    }
    window.localStorage.setItem("pickle.session.guestId", me.guestId);
  } catch {
    /* ignore */
  }
}

export function getSessionToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getStoredMe(): Attendee | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SESSION_ME_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Attendee;
  } catch {
    return null;
  }
}

export function getStoredRoster(): readonly Attendee[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SESSION_ROSTER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Attendee[]) : null;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(SESSION_TOKEN_KEY);
    window.sessionStorage.removeItem(SESSION_ME_KEY);
    window.sessionStorage.removeItem(SESSION_ROSTER_KEY);
    window.localStorage.removeItem("pickle.session.guestId");
  } catch {
    /* ignore */
  }
}

export async function restoreSession(): Promise<{
  me: Attendee;
  roster: readonly Attendee[];
} | null> {
  const token = getSessionToken();
  const me = getStoredMe();
  if (!token || !me) return null;

  try {
    const roster = await fetchRoster(token);
    persistSession(token, me, roster);
    return { me, roster };
  } catch (e) {
    captureApiFailure("restore", e);
    const cached = getStoredRoster();
    if (cached && cached.length > 0) {
      return { me, roster: cached };
    }
    clearSession();
    return null;
  }
}
